/**
 * incidentCategoryCascade.js — shared cascade dimension + tree helpers.
 *
 * Runtime-agnostic (no React/Node-only imports). Consumed by:
 *   - configSchema.js (validation + diff)
 *   - frontend IncidentCategoriesEditor + eventConfig.js
 */

const norm = (v) => String(v ?? '').trim().toLowerCase();

/** Treatments that participate in the incident-category cascade. */
export const CASCADE_EXCLUDED_TREATMENTS = new Set(['Other']);

export function sortedSeverityValues(severityCodes) {
  return [...(severityCodes || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(c => c.value)
    .filter(Boolean);
}

export function sortedTreatments(treatmentTypes) {
  return [...(treatmentTypes || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter(t => t?.value && !CASCADE_EXCLUDED_TREATMENTS.has(t.value));
}

export function buildCodeSet(severityCodes) {
  return new Set(sortedSeverityValues(severityCodes));
}

export function buildTreatmentSet(treatmentTypes) {
  return new Set(sortedTreatments(treatmentTypes).map(t => t.value));
}

/**
 * The declared subtypes for a treatment row, normalised to a clean string list.
 * `subtypes` is a first-class field on a treatmentTypes row (e.g. AWI →
 * ['Toxin','Filler']). Absent / empty ⇒ the treatment is flat (code-keyed).
 */
export function declaredSubtypes(treatmentType) {
  const raw = treatmentType?.subtypes;
  if (!Array.isArray(raw)) return [];
  return raw.map(s => String(s ?? '').trim()).filter(Boolean);
}

/** Sentinel select value — free text is stored in treatment_subtype, not as "Other". */
export const SUBTYPE_OTHER = 'Other';

/** Value persisted on the case row from a subtype picker + optional Other text. */
export function resolveTreatmentSubtypeValue(selectValue, otherText) {
  if (selectValue === SUBTYPE_OTHER) return String(otherText ?? '').trim() || null;
  return String(selectValue ?? '').trim() || null;
}

/** Map a stored subtype back to { select, other } for edit/create forms. */
export function treatmentSubtypeToForm(stored, predefinedOptions = []) {
  const v = String(stored ?? '').trim();
  if (!v) return { select: '', other: '' };
  if (predefinedOptions.includes(v)) return { select: v, other: '' };
  return { select: SUBTYPE_OTHER, other: v };
}

/**
 * Infer the subtypes of a LEGACY treatment node purely from its tree shape — a
 * subtype's value is an OBJECT (a code→categories map); a code's value is an
 * ARRAY. Used ONLY by the one-time migration below, never by the runtime read.
 * A typo'd code (array value) is NOT mistaken for a subtype, so the typo still
 * surfaces as an unknown severity code at validation time.
 */
function inferSubtypesFromNode(node, codeSet) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return [];
  const objectKeys = Object.keys(node).filter(
    k => node[k] && typeof node[k] === 'object' && !Array.isArray(node[k]),
  );
  return objectKeys.filter(k => !codeSet.has(k)).sort();
}

/**
 * SINGLE source of truth: a treatment is nested IFF its row declares `subtypes`.
 * No tree-shape guessing happens here — legacy rows are normalised up-front by
 * `migrateSubtypeDeclarations` so the field is ALWAYS present before any read.
 * Both validation-time and the runtime cascade read call this same function.
 * @returns {string[]} [] ⇒ flat (code-keyed) treatment.
 */
export function resolveSubtypes(treatmentType /* , node, codeSet (legacy args, ignored) */) {
  return declaredSubtypes(treatmentType);
}

/**
 * Default/migrate every treatmentTypes row so `subtypes` is ALWAYS declared.
 * Rows that already declare the field (incl. an explicit `[]` = flat) are left
 * untouched; rows missing it have their subtypes inferred ONCE from the tree
 * shape and written onto the row. After this runs the rest of the system treats
 * the declared field as the only source of truth — there is no live inference.
 *
 * @param {Array} treatmentTypes
 * @param {object} tree     the incident-category tree (for inference of legacy rows)
 * @param {Array}  severityCodes
 * @returns {Array} a new treatmentTypes array with `subtypes` declared on every row
 */
export function migrateSubtypeDeclarations(treatmentTypes, tree, severityCodes) {
  const codeSet = buildCodeSet(severityCodes);
  return (treatmentTypes || []).map(tx => {
    if (!tx || typeof tx !== 'object') return tx;
    if (Array.isArray(tx.subtypes)) return tx;               // already declared (incl. [])
    const inferred = inferSubtypesFromNode(tree?.[tx.value], codeSet);
    return { ...tx, subtypes: inferred };                    // legacy row → declare it
  });
}

/**
 * Subtype list for ONE treatment VALUE (e.g. 'AWI' → ['Toxin','Filler']) — the
 * config-driven lookup the downstream new-case form uses to render its subtype
 * picker. Reads the declared `subtypes` field; for legacy rows the value is
 * defaulted from the tree first, so the read itself never guesses.
 * @returns {string[]} [] ⇒ the treatment has no subtypes (skip the picker).
 */
export function subtypesForTreatment(treatmentTypes, treatmentValue, tree, severityCodes) {
  if (!treatmentValue) return [];
  const migrated = migrateSubtypeDeclarations(treatmentTypes, tree, severityCodes);
  const tx = migrated.find(t => t?.value === treatmentValue);
  if (!tx) return [];
  return resolveSubtypes(tx);
}

/**
 * Derive editor blocks from config dimensions. Subtypes come FIRST from the
 * declared `subtypes` field on each treatment row, then (legacy) from tree shape.
 * @returns {Array<{ treatment: string, label: string, subtypes: string[]|null, codes: string[] }>}
 */
export function deriveCascadeBlocks(treatmentTypes, severityCodes, tree) {
  const codeSet = buildCodeSet(severityCodes);
  const codes = [...codeSet];
  // Default legacy rows so `subtypes` is declared, then read the declared field —
  // the editor blocks and the runtime cascade derive from the SAME source.
  const migrated = migrateSubtypeDeclarations(treatmentTypes, tree, severityCodes);

  return sortedTreatments(migrated).map(tx => {
    const subtypes = resolveSubtypes(tx);
    return {
      treatment: tx.value,
      label: tx.label || tx.value,
      subtypes: subtypes.length ? subtypes : null,
      codes,
    };
  });
}

/** Cell path key for validation error mapping: "LHR|Code 3" or "AWI|Toxin|Code 5". */
export function cellKey(treatment, subtype, code) {
  return subtype ? `${treatment}|${subtype}|${code}` : `${treatment}|${code}`;
}

export function cellLabel(treatment, subtype, code) {
  return subtype ? `${treatment} · ${subtype} · ${code}` : `${treatment} · ${code}`;
}

/**
 * Read categories for one cascade cell. The CALLER's `subtype` argument is the
 * single source of truth for nesting — it is derived from the treatment's
 * declared `subtypes` (deriveCascadeBlocks / subtypesForTreatment), so this read
 * does NOT guess from tree shape: a truthy `subtype` reads the nested branch, a
 * falsy one reads the flat branch. (codeSet kept for signature compatibility.)
 */
export function getCellCategories(tree, treatment, subtype, code, codeSet) { // eslint-disable-line no-unused-vars
  const node = tree?.[treatment];
  if (!node || typeof node !== 'object' || Array.isArray(node)) return [];
  if (subtype) {
    const sub = node[subtype];
    if (!sub || typeof sub !== 'object' || Array.isArray(sub)) return [];
    const list = sub[code];
    return Array.isArray(list) ? list : [];
  }
  const list = node[code];
  return Array.isArray(list) ? list : [];
}

/**
 * Enumerate all cells present in a tree (for diff / validation walks).
 */
export function enumerateCells(tree, treatmentTypes, severityCodes) {
  const blocks = deriveCascadeBlocks(treatmentTypes, severityCodes, tree || {});
  const cells = [];
  for (const block of blocks) {
    if (block.subtypes) {
      for (const subtype of block.subtypes) {
        for (const code of block.codes) {
          cells.push({
            treatment: block.treatment,
            subtype,
            code,
            path: cellKey(block.treatment, subtype, code),
            label: cellLabel(block.treatment, subtype, code),
          });
        }
      }
    } else {
      for (const code of block.codes) {
        cells.push({
          treatment: block.treatment,
          subtype: null,
          code,
          path: cellKey(block.treatment, null, code),
          label: cellLabel(block.treatment, null, code),
        });
      }
    }
  }
  return cells;
}

/**
 * Trim category strings, drop empties, prune unknown treatment keys, and align
 * the tree to each treatment's DECLARED subtypes — the same single source of
 * truth validation uses.
 *
 * The published tree is ALIGNED to the declared model (the same source of truth
 * validation uses): a declared-flat treatment keeps only code-keyed arrays; a
 * declared-nested treatment keeps ONLY its declared subtype branches. This means
 * the editor must never hand an undeclared (orphan) branch to publish — it stashes
 * "kept" branches in editor state instead. Does not inject missing code slots.
 *
 * @param {object} tree
 * @param {Array}  treatmentTypes  rows whose declared `subtypes` drive shape.
 *   Legacy rows should be migrated (migrateSubtypeDeclarations) before this call;
 *   an absent field is treated as flat here.
 */
export function normalizeCategoryTree(tree, treatmentTypes) {
  const allowed = buildTreatmentSet(treatmentTypes);
  const byValue = new Map((treatmentTypes || []).map(t => [t?.value, t]));
  const clean = {};
  if (!tree || typeof tree !== 'object' || Array.isArray(tree)) return clean;

  const cleanCodeMap = (obj) => {
    const out = {};
    for (const [code, list] of Object.entries(obj)) {
      if (Array.isArray(list)) out[code] = list.map(s => String(s).trim()).filter(Boolean);
    }
    return out;
  };

  for (const [treatment, node] of Object.entries(tree)) {
    if (!allowed.has(treatment)) continue;
    if (!node || typeof node !== 'object' || Array.isArray(node)) continue;

    const declared = declaredSubtypes(byValue.get(treatment));
    let cleaned;
    if (declared.length) {
      // Nested: keep ONLY declared subtype branches (drop undeclared/orphan keys
      // and stray code-keyed arrays at the top level).
      cleaned = {};
      for (const sub of declared) {
        const branch = node[sub];
        if (branch && typeof branch === 'object' && !Array.isArray(branch)) {
          cleaned[sub] = cleanCodeMap(branch);
        }
      }
    } else {
      // Flat: keep only code-keyed arrays (drop any stray object branches).
      cleaned = cleanCodeMap(node);
    }
    if (Object.keys(cleaned).length) clean[treatment] = cleaned;
  }
  return clean;
}

/** Compare two category arrays; detect add/remove/reorder. */
export function diffCategoryLists(oldList, newList) {
  const oldArr = oldList || [];
  const newArr = newList || [];
  const oldSet = new Set(oldArr.map(norm));
  const newSet = new Set(newArr.map(norm));

  const added = newArr.filter(c => !oldSet.has(norm(c)));
  const removed = oldArr.filter(c => !newSet.has(norm(c)));
  const reordered = added.length === 0 && removed.length === 0
    && oldArr.length === newArr.length
    && oldArr.some((c, i) => c !== newArr[i]);

  return {
    added,
    removed,
    reordered,
    changed: added.length > 0 || removed.length > 0 || reordered,
  };
}

export { norm as normCategory };
